/**
 * Track B — Legacy-cohort timezone banner.
 *
 * Dismissible dashboard-top alert prompting users whose profile
 * `timezone` field is unset to confirm it via the settings/profile
 * page. Reach: legacy pre-Track-B signups (57% of providers, 37% of
 * customers per Phase O backfill telemetry) whose profile.timezone
 * was left null and are currently being resolved via state → ET.
 *
 * Visibility rule (locked from Track B dispatch):
 *   - Shown only when `profileTimezone` is unset (null / empty).
 *   - Session-dismissible via X button — flag stored in
 *     `sessionStorage["pp_tz_banner_dismissed_v1"]`. Clears on
 *     browser session end, so the banner returns next login.
 *   - Setting the timezone via settings/profile page hides
 *     permanently (visibility condition itself flips).
 *
 * Props:
 *   - `profileTimezone`: string | null | undefined — the current
 *     profile.timezone value from the caller's already-fetched
 *     /profiles/{role}/me payload.
 *   - `settingsHref`: string — where the CTA button navigates
 *     (`/dashboard/customer/settings` or `/dashboard/provider/profile`).
 *
 * The banner does NOT fetch its own data — it consumes the parent's
 * profile state to avoid duplicate network calls.
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export const TZ_BANNER_DISMISS_KEY = "pp_tz_banner_dismissed_v1";

function _readDismissed() {
  try {
    return sessionStorage.getItem(TZ_BANNER_DISMISS_KEY) === "true";
  } catch (_) {
    // Private-browsing / disabled storage — treat as not-dismissed
    // rather than hiding the banner outright. The user can still
    // reach the settings page via the topbar dropdown.
    return false;
  }
}

function _writeDismissed() {
  try {
    sessionStorage.setItem(TZ_BANNER_DISMISS_KEY, "true");
  } catch (_) { /* private browsing — no-op, banner will re-render on next mount */ }
}

export default function TimezoneBanner({ profileTimezone, settingsHref }) {
  const [dismissed, setDismissed] = useState(() => _readDismissed());

  // Re-check the dismiss flag whenever profileTimezone changes so a
  // save-driven refetch immediately hides the banner (independent of
  // sessionStorage).
  useEffect(() => {
    if (profileTimezone) return; // hidden by the visibility rule anyway
    setDismissed(_readDismissed());
  }, [profileTimezone]);

  // Visibility rule: hide when timezone IS set or user has dismissed
  // this session.
  if (profileTimezone) return null;
  if (dismissed) return null;

  const onDismiss = () => {
    _writeDismissed();
    setDismissed(true);
  };

  return (
    <Alert
      className="mb-4 flex items-start justify-between gap-3 border-pp-orange/40 bg-pp-orange/10"
      data-testid="tz-banner"
    >
      <div className="flex-1 min-w-0">
        <AlertDescription className="text-pp-cta-text">
          <span className="font-medium">Confirm your timezone</span>{" "}
          <span className="opacity-80">
            so reminders arrive at the right time.
          </span>
        </AlertDescription>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          asChild
          size="sm"
          variant="secondary"
          data-testid="tz-banner-cta"
        >
          <Link to={settingsHref}>Set timezone</Link>
        </Button>
        <button
          type="button"
          className="p-1 rounded hover:bg-pp-orange/20 transition"
          aria-label="Dismiss timezone banner"
          data-testid="tz-banner-dismiss"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </Alert>
  );
}
